import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LoadingOverlay from "../components/Loading";
import KeyLoanEditPopup from "../components/KeyLoanEditPopup";
import KeyLoanHistoryPopup from "../components/KeyLoanHistoryPopup";
import { useConfirm } from "../confirm/ConfirmProvider";
import { useUser } from "../contexts/UserContext";
import {
  borrowKey,
  getKeyLoanList,
  getKeyStatusList,
  returnKey,
} from "../fetch/KeyRequest";
import { errorMessage } from "../function/errorMessage";
import { getLastLab } from "../function/lastLab";
import { toJSTDateTimeString } from "../function/toJSTDateString";
import styles from "../styles/modules/ListPage.module.css";
import keyStyles from "../styles/modules/KeyPage.module.css";
import { toast } from "../toast/toast";
import { KeyLoanDisp, KeyStatus, Lab } from "../types";

type TabMode = "status" | "history";

function KeyPage() {
  const { user, userLabs, memberships } = useUser();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();
  const stateLabId = (location.state as { labId?: number } | null)?.labId;

  const [tab, setTab] = useState<TabMode>("status");
  const [keyStatusList, setKeyStatusList] = useState<KeyStatus[]>([]);
  const [loans, setLoans] = useState<KeyLoanDisp[]>([]);
  const [targetLoan, setTargetLoan] = useState<KeyLoanDisp | null>(null);
  // 「修正履歴」を押したときに開く履歴ポップアップ（対象の記録と項目）
  const [historyTarget, setHistoryTarget] = useState<{
    loan: KeyLoanDisp;
    field: "borrowed_at" | "returned_at";
  } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // 研究室は遷移元の指定、なければ「最後に選んだ研究室」を使う。
  const selectedLab: Lab | null = useMemo(() => {
    if (userLabs.length === 0) return null;
    const lastLabId = user ? getLastLab(user.id) : null;
    return (
      userLabs.find((lab) => lab.id === stateLabId) ??
      userLabs.find((lab) => lab.id === lastLabId) ??
      null
    );
  }, [user, userLabs, stateLabId]);

  const isLabAdmin = useMemo(
    () =>
      !!selectedLab &&
      memberships.some(
        (m) => m.role_code === "lab_admin" && m.lab_id === selectedLab.id
      ),
    [memberships, selectedLab]
  );

  // 所属研究室が無い、または特定できない場合は研究室選択へ戻す。
  useEffect(() => {
    if (userLabs.length === 0 || !selectedLab) {
      navigate("/labs", { replace: true });
    }
  }, [userLabs, selectedLab, navigate]);

  useEffect(() => {
    if (selectedLab) load(selectedLab.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLab]);

  async function load(lab_id: number) {
    setIsLoading(true);
    const [statusList, loanList] = await Promise.all([
      getKeyStatusList(lab_id),
      getKeyLoanList(lab_id),
    ]);
    setKeyStatusList(statusList);
    setLoans(loanList);
    setIsLoading(false);
  }

  async function handleBorrow(status: KeyStatus) {
    if (!selectedLab) return;
    setIsLoading(true);
    try {
      await borrowKey(status.storage_location_id);
      toast.success(`${status.storage_location_name}の鍵を借りました。`);
      await load(selectedLab.id);
    } catch (error) {
      errorMessage("鍵の貸出に失敗しました。", error);
      await load(selectedLab.id);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleReturn(status: KeyStatus) {
    if (!selectedLab || status.loan_id === null) return;

    const isSelf = status.user_id === user?.id;
    if (
      !(await confirm(
        isSelf
          ? `${status.storage_location_name}の鍵を返却します。よろしいですか？`
          : `${status.user_name ?? "他のユーザー"}さんが借りている鍵を代理で返却します。よろしいですか？`,
        { confirmText: "返却" }
      ))
    ) {
      return;
    }

    setIsLoading(true);
    try {
      await returnKey(status.loan_id);
      toast.success(`${status.storage_location_name}の鍵を返却しました。`);
      await load(selectedLab.id);
    } catch (error) {
      errorMessage("鍵の返却に失敗しました。", error);
      await load(selectedLab.id);
    } finally {
      setIsLoading(false);
    }
  }

  // 自分の記録は本人、他の人の記録は研究室管理者のみ修正できる。
  function canEditLoan(loan: KeyLoanDisp): boolean {
    return loan.user_id === user?.id || isLabAdmin;
  }

  function download() {
    try {
      const csvHeader = [
        "No",
        "試薬庫",
        "学籍番号",
        "使用者",
        "貸出時間",
        "返却時間",
        "修正の有無",
      ];

      const csvRows = loans.map((loan, index) => [
        index + 1,
        loan.storage_location_name ?? "",
        loan.user_num ?? "",
        loan.user_name ?? "",
        toJSTDateTimeString(loan.borrowed_at),
        loan.returned_at ? toJSTDateTimeString(loan.returned_at) : "未返却",
        loan.borrowed_edited || loan.returned_edited ? "修正済み" : "",
      ]);

      const csvContent =
        "﻿" +
        [csvHeader, ...csvRows]
          .map((row) => row.map((val) => `${val}`).join(","))
          .join("\n");

      const now = new Date();
      const pad = (n: number) => n.toString().padStart(2, "0");
      const nowDay = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(
        now.getDate()
      )}${pad(now.getHours())}${pad(now.getMinutes())}`;

      // どの研究室の履歴か分かるよう、ファイル名に研究室名を入れる
      // （試薬の履歴CSVで試薬名を入れているのと同じ方式）
      const labName = (selectedLab?.name ?? "")
        .replace(/[\\/:*?"<>|]/g, "")
        .trim()
        .slice(0, 150);

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = labName
        ? `key_loans_${labName}_${nowDay}.csv`
        : `key_loans_${nowDay}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      errorMessage("CSVのダウンロードでエラーが発生しました。", error);
    }
  }

  return (
    <>
      <div className={styles["list-page"]}>
        <div className={styles["content"]}>
          <div className={styles["title-block"]}>
            <h2 className={styles["page-title"]}>鍵管理</h2>
            {selectedLab && <p className={styles["subtitle"]}>{selectedLab.name}</p>}
          </div>

          <div className={keyStyles["tab-row"]}>
            <div className={keyStyles["tab-toggle"]}>
              <button
                type="button"
                className={tab === "status" ? keyStyles["active"] : undefined}
                onClick={() => setTab("status")}
              >
                貸出状況
              </button>
              <button
                type="button"
                className={tab === "history" ? keyStyles["active"] : undefined}
                onClick={() => setTab("history")}
              >
                貸出履歴
              </button>
            </div>
          </div>

          {tab === "status" && (
            <>
              {keyStatusList.length === 0 ? (
                <p className={styles["empty"]}>
                  鍵管理を行う試薬庫がありません。
                  {isLabAdmin
                    ? "研究室選択画面の「保管場所を管理」から設定してください。"
                    : "研究室管理者にお問い合わせください。"}
                </p>
              ) : (
                <div className={styles["table-wrap"]}>
                  <table className={styles["table"]}>
                    <thead>
                      <tr>
                        <th>試薬庫</th>
                        <th>状態</th>
                        <th>使用者</th>
                        <th>貸出時間</th>
                        <th>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {keyStatusList.map((status) => {
                        const isLent = status.loan_id !== null;
                        const isMine = isLent && status.user_id === user?.id;
                        return (
                          <tr key={status.storage_location_id}>
                            <td>{status.storage_location_name}</td>
                            <td>
                              <span
                                className={`${keyStyles["badge"]} ${
                                  isLent ? keyStyles["lend"] : keyStyles["keep"]
                                }`}
                              >
                                {isLent ? "貸出中" : "保管中"}
                              </span>
                            </td>
                            <td>
                              {isLent
                                ? `${status.user_name ?? "-"}${isMine ? "（自分）" : ""}`
                                : "-"}
                            </td>
                            <td>
                              {status.borrowed_at
                                ? toJSTDateTimeString(status.borrowed_at)
                                : "-"}
                            </td>
                            <td>
                              {!isLent && (
                                <button
                                  type="button"
                                  className={keyStyles["borrow"]}
                                  onClick={() => handleBorrow(status)}
                                >
                                  借りる
                                </button>
                              )}
                              {isLent && (isMine || isLabAdmin) && (
                                <button
                                  type="button"
                                  className={keyStyles["return"]}
                                  onClick={() => handleReturn(status)}
                                >
                                  {isMine ? "返却" : "代理返却"}
                                </button>
                              )}
                              {isLent && !isMine && !isLabAdmin && (
                                <span className={keyStyles["disabled"]}>貸出中</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <p className={keyStyles["note"]}>
                ・「借りる」「返却」を押した時刻が、ログイン中のユーザーとあわせて自動で記録されます。
                <br />
                ・鍵管理中の試薬庫の試薬は、その鍵を借りている人だけが使用・返却できます。
              </p>
            </>
          )}

          {tab === "history" && (
            <>
              {loans.length === 0 ? (
                <p className={styles["empty"]}>貸出履歴がありません。</p>
              ) : (
                <>
                  <div className={styles["table-wrap"]}>
                    <table className={styles["table"]}>
                      <thead>
                        <tr>
                          <th>試薬庫</th>
                          <th>学籍番号</th>
                          <th>使用者</th>
                          <th>貸出時間</th>
                          <th>返却時間</th>
                          <th>操作</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loans.map((loan) => (
                          <tr key={loan.id}>
                            <td>{loan.storage_location_name ?? "-"}</td>
                            <td>{loan.user_num ?? "-"}</td>
                            <td>{loan.user_name ?? "-"}</td>
                            <td>
                              {toJSTDateTimeString(loan.borrowed_at)}
                              {loan.borrowed_edited && (
                                <button
                                  type="button"
                                  className={keyStyles["edited"]}
                                  onClick={() =>
                                    setHistoryTarget({
                                      loan,
                                      field: "borrowed_at",
                                    })
                                  }
                                >
                                  修正履歴
                                  {loan.borrowed_edit_count > 1 &&
                                    ` ${loan.borrowed_edit_count}件`}
                                  {" ›"}
                                </button>
                              )}
                            </td>
                            <td>
                              {loan.returned_at
                                ? toJSTDateTimeString(loan.returned_at)
                                : "未返却"}
                              {loan.returned_edited && (
                                <button
                                  type="button"
                                  className={keyStyles["edited"]}
                                  onClick={() =>
                                    setHistoryTarget({
                                      loan,
                                      field: "returned_at",
                                    })
                                  }
                                >
                                  修正履歴
                                  {loan.returned_edit_count > 1 &&
                                    ` ${loan.returned_edit_count}件`}
                                  {" ›"}
                                </button>
                              )}
                            </td>
                            <td>
                              {canEditLoan(loan) && (
                                <button
                                  type="button"
                                  className={keyStyles["edit"]}
                                  onClick={() => setTargetLoan(loan)}
                                >
                                  修正
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className={keyStyles["csv-row"]}>
                    <button
                      type="button"
                      className={keyStyles["csv-download"]}
                      onClick={download}
                    >
                      CSVダウンロード
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {targetLoan && (
        <KeyLoanEditPopup
          loan={targetLoan}
          onClose={() => setTargetLoan(null)}
          onSaved={() => {
            setTargetLoan(null);
            if (selectedLab) load(selectedLab.id);
          }}
        />
      )}

      {historyTarget && (
        <KeyLoanHistoryPopup
          loan={historyTarget.loan}
          field={historyTarget.field}
          onClose={() => setHistoryTarget(null)}
        />
      )}

      {isLoading && <LoadingOverlay />}
    </>
  );
}

export default KeyPage;
